import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchGitHubRepoForImport,
  importGitHubCookbook,
  messageForGitHubImportError,
  parseGitHubRepoUrlClient,
} from '../../services/githubImportService';
import {
  fetchCookbookSaveQuota,
  fetchManualSnippetCreationQuota,
  messageForCookbookQuotaCheckUnavailable,
  messageSnippetCreationMonthlyLimitReached,
} from '../../services/subscriptionQuotaService';
import {
  FREE_COOKBOOK_CREATIONS_PER_MONTH,
  FREE_MANUAL_SNIPPET_CREATIONS_PER_MONTH,
} from '../../constants/subscriptionTier';
import { cookbookDeckRemovedByMonthlyLimitMessage } from '../../utils/cookbookDeckQuotaTriggerWatch';
import './GitHubImportModal.css';

function buildEditableCards(cards, maxInclude = null) {
  return (cards || []).map((card, index) => ({
    key: `${card.path || 'card'}-${index}`,
    included: maxInclude == null ? true : index < maxInclude,
    title: card.title || 'Untitled',
    description: card.description || '',
    solution: card.solution || '',
    language: card.language || 'Other',
    tags: Array.isArray(card.tags) ? card.tags : ['github'],
    path: card.path || '',
  }));
}

const GitHubImportModal = ({ userId, open, onClose, onImported }) => {
  const [repoUrl, setRepoUrl] = useState('');
  const [pathPrefix, setPathPrefix] = useState('');
  const [step, setStep] = useState('form'); // form | preview | importing
  const [preview, setPreview] = useState(null);
  const [cookbookName, setCookbookName] = useState('');
  const [editableCards, setEditableCards] = useState([]);
  const [cookbookQuota, setCookbookQuota] = useState(null);
  const [snippetQuota, setSnippetQuota] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setRepoUrl('');
    setPathPrefix('');
    setStep('form');
    setPreview(null);
    setCookbookName('');
    setEditableCards([]);
    setLoading(false);
    setError('');
    setCookbookQuota(null);
    setSnippetQuota(null);

    let cancelled = false;
    const loadQuota = async () => {
      setQuotaLoading(true);
      try {
        const [cookbook, snippet] = await Promise.all([
          fetchCookbookSaveQuota(),
          fetchManualSnippetCreationQuota(),
        ]);
        if (!cancelled) {
          setCookbookQuota(cookbook);
          setSnippetQuota(snippet);
        }
      } catch {
        if (!cancelled) {
          setCookbookQuota(null);
          setSnippetQuota(null);
        }
      } finally {
        if (!cancelled) setQuotaLoading(false);
      }
    };
    loadQuota();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const includedCards = useMemo(
    () => editableCards.filter((card) => card.included),
    [editableCards]
  );

  const snippetRemaining =
    snippetQuota && !snippetQuota.isPremium && !snippetQuota.quotaCheckUnavailable
      ? snippetQuota.remaining != null
        ? snippetQuota.remaining
        : snippetQuota.allowed === false
          ? 0
          : null
      : null;

  const cookbookBlocked =
    cookbookQuota &&
    !cookbookQuota.quotaCheckUnavailable &&
    !cookbookQuota.isPremium &&
    cookbookQuota.allowed === false;

  const snippetBlocked =
    snippetQuota &&
    !snippetQuota.quotaCheckUnavailable &&
    !snippetQuota.isPremium &&
    (snippetQuota.allowed === false || snippetRemaining === 0);

  const quotaAlerts = useMemo(() => {
    if (quotaLoading) return [];
    const alerts = [];

    if (cookbookQuota?.quotaCheckUnavailable) {
      alerts.push({
        id: 'cookbook-unavailable',
        tone: 'error',
        text: messageForCookbookQuotaCheckUnavailable(cookbookQuota),
        showUpgrade: false,
      });
    } else if (cookbookQuota && !cookbookQuota.isPremium) {
      const limit = cookbookQuota.limit ?? FREE_COOKBOOK_CREATIONS_PER_MONTH;
      const remaining =
        cookbookQuota.remaining != null
          ? cookbookQuota.remaining
          : cookbookQuota.allowed === false
            ? 0
            : null;
      const used =
        cookbookQuota.createdThisMonth != null
          ? cookbookQuota.createdThisMonth
          : remaining != null
            ? Math.max(0, limit - remaining)
            : null;

      if (cookbookQuota.allowed === false || remaining === 0) {
        alerts.push({
          id: 'cookbook-blocked',
          tone: 'error',
          text: `Free plan limit reached: ${limit} cookbooks this month. Upgrade to Pro for unlimited imports.`,
          showUpgrade: true,
        });
      } else if (remaining === 1 || (used != null && used >= limit - 1)) {
        alerts.push({
          id: 'cookbook-warning',
          tone: 'warning',
          text: `Free plan: ${remaining != null ? remaining : 1} of ${limit} cookbook${limit === 1 ? '' : 's'} left this month.`,
          showUpgrade: true,
        });
      } else if (used != null) {
        alerts.push({
          id: 'cookbook-info',
          tone: 'info',
          text: `Free plan: ${used}/${limit} cookbooks used this month.`,
          showUpgrade: false,
        });
      }
    }

    if (snippetQuota?.quotaCheckUnavailable) {
      alerts.push({
        id: 'snippet-unavailable',
        tone: 'error',
        text: 'Could not verify your snippet limit. Check your connection and try again.',
        showUpgrade: false,
      });
    } else if (snippetQuota && !snippetQuota.isPremium) {
      const limit = snippetQuota.limit ?? FREE_MANUAL_SNIPPET_CREATIONS_PER_MONTH;
      const remaining =
        snippetQuota.remaining != null
          ? snippetQuota.remaining
          : snippetQuota.allowed === false
            ? 0
            : null;
      const used =
        snippetQuota.usedThisMonth != null
          ? snippetQuota.usedThisMonth
          : remaining != null
            ? Math.max(0, limit - remaining)
            : null;

      if (snippetQuota.allowed === false || remaining === 0) {
        alerts.push({
          id: 'snippet-blocked',
          tone: 'error',
          text: messageSnippetCreationMonthlyLimitReached(snippetQuota),
          showUpgrade: true,
        });
      } else if (remaining != null && remaining <= 3) {
        alerts.push({
          id: 'snippet-warning',
          tone: 'warning',
          text: `Free plan: ${remaining} of ${limit} snippets left this month. Imported files count toward this limit.`,
          showUpgrade: true,
        });
      } else if (used != null) {
        alerts.push({
          id: 'snippet-info',
          tone: 'info',
          text: `Free plan: ${used}/${limit} snippets used this month. Imported files count toward this limit.`,
          showUpgrade: false,
        });
      }
    }

    return alerts;
  }, [cookbookQuota, snippetQuota, quotaLoading]);

  const handlePreview = async () => {
    if (!repoUrl.trim() || loading) return;
    setLoading(true);
    setError('');
    try {
      const [data, freshCookbookQuota, freshSnippetQuota] = await Promise.all([
        fetchGitHubRepoForImport({
          repoUrl: repoUrl.trim(),
          pathPrefix: pathPrefix.trim(),
          maxFiles: 40,
        }),
        fetchCookbookSaveQuota().catch(() => cookbookQuota),
        fetchManualSnippetCreationQuota().catch(() => snippetQuota),
      ]);
      if (freshCookbookQuota) setCookbookQuota(freshCookbookQuota);
      if (freshSnippetQuota) setSnippetQuota(freshSnippetQuota);

      const maxInclude =
        freshSnippetQuota &&
        !freshSnippetQuota.isPremium &&
        !freshSnippetQuota.quotaCheckUnavailable &&
        freshSnippetQuota.remaining != null
          ? Math.max(0, freshSnippetQuota.remaining)
          : null;

      setPreview(data);
      setCookbookName(data.repo || data.fullName || 'Imported cookbook');
      setEditableCards(buildEditableCards(data.cards, maxInclude));
      setStep('preview');

      if (maxInclude != null && data.cards.length > maxInclude) {
        setError(
          maxInclude === 0
            ? 'Snippet limit reached — uncheck all or upgrade to import.'
            : `Only ${maxInclude} snippet${maxInclude === 1 ? '' : 's'} left this month. Extra files were unchecked.`
        );
      }
    } catch (err) {
      const mapped = messageForGitHubImportError(err);
      setError(mapped.text);
      if (mapped.type === 'upgrade') {
        window.dispatchEvent(new CustomEvent('navigateToSubscription'));
      }
    } finally {
      setLoading(false);
    }
  };

  const updateCardTitle = (key, title) => {
    setEditableCards((prev) =>
      prev.map((card) => (card.key === key ? { ...card, title } : card))
    );
  };

  const toggleCardIncluded = (key) => {
    setEditableCards((prev) => {
      const target = prev.find((card) => card.key === key);
      if (!target) return prev;

      if (!target.included && snippetRemaining != null) {
        const currentlyIncluded = prev.filter((card) => card.included).length;
        if (currentlyIncluded >= snippetRemaining) {
          setError(
            `Free plan: only ${snippetRemaining} snippet${snippetRemaining === 1 ? '' : 's'} left this month.`
          );
          return prev;
        }
      }

      setError('');
      return prev.map((card) =>
        card.key === key ? { ...card, included: !card.included } : card
      );
    });
  };

  const handleConfirmImport = async () => {
    if (!preview || !userId || loading) return;
    if (!cookbookName.trim()) {
      setError('Enter a cookbook name.');
      return;
    }
    if (includedCards.length === 0) {
      setError('Include at least one snippet to import.');
      return;
    }
    if (cookbookBlocked) {
      setError('Cookbook limit reached. Upgrade to Pro to import more.');
      return;
    }
    if (snippetBlocked) {
      setError(messageSnippetCreationMonthlyLimitReached(snippetQuota));
      return;
    }
    if (snippetRemaining != null && includedCards.length > snippetRemaining) {
      setError(
        `Free plan: you can import at most ${snippetRemaining} snippet${snippetRemaining === 1 ? '' : 's'} this month.`
      );
      return;
    }

    setLoading(true);
    setError('');
    setStep('importing');
    try {
      const result = await importGitHubCookbook(userId, {
        ...preview,
        name: cookbookName.trim(),
        cards: includedCards.map(({ key, included, ...card }) => card),
      });
      if (!result.ok) {
        if (result.reason === 'quota_unavailable') {
          setError(messageForCookbookQuotaCheckUnavailable(result.quota));
        } else if (result.reason === 'quota_exceeded') {
          setError('Cookbook limit reached. Upgrade to Pro to import more.');
          setCookbookQuota(result.quota || { ...cookbookQuota, allowed: false, remaining: 0 });
        } else if (result.reason === 'snippet_quota_exceeded') {
          setError(messageSnippetCreationMonthlyLimitReached(result.quota));
          setSnippetQuota(result.quota || snippetQuota);
        } else if (result.reason === 'snippet_quota_unavailable') {
          setError('Could not verify your snippet limit. Check your connection and try again.');
        } else if (result.reason === 'removed_by_quota_trigger') {
          setError(cookbookDeckRemovedByMonthlyLimitMessage);
        } else {
          setError('Import failed. Try again.');
        }
        setStep('preview');
        return;
      }
      onImported?.(result);
      onClose();
    } catch (err) {
      setError('Import failed. Try again.');
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const canCreate =
    Boolean(preview) &&
    cookbookName.trim().length > 0 &&
    includedCards.length > 0 &&
    !cookbookBlocked &&
    !snippetBlocked &&
    !(snippetRemaining != null && includedCards.length > snippetRemaining) &&
    !loading;

  return (
    <div className="github-import-modal__overlay" onClick={onClose}>
      <div
        className="github-import-modal__panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="github-import-modal-title"
      >
        <button
          type="button"
          className="github-import-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <h2 id="github-import-modal-title" className="github-import-modal__title">
          Import git repo
        </h2>
        <p className="github-import-modal__subtitle">
          Turn a public GitHub repository into a study cookbook.
        </p>

        {quotaAlerts.map((alert) => (
          <div
            key={alert.id}
            className={`github-import-modal__quota-alert github-import-modal__quota-alert--${alert.tone}`}
            role="status"
          >
            <span className="github-import-modal__quota-alert-text">{alert.text}</span>
            {alert.showUpgrade && (
              <button
                type="button"
                className="github-import-modal__quota-alert-action"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent('navigateToSubscription'))
                }
              >
                Upgrade
              </button>
            )}
          </div>
        ))}

        {step === 'form' && (
          <>
            <label className="github-import-modal__label">
              Repository URL or owner/repo
              <input
                className="github-import-modal__input"
                value={repoUrl}
                onChange={(e) => {
                  const next = e.target.value;
                  setRepoUrl(next);
                  const parsed = parseGitHubRepoUrlClient(next);
                  if (parsed?.pathPrefix) {
                    setPathPrefix(parsed.pathPrefix);
                  }
                }}
                placeholder="https://github.com/owner/repo/tree/main/src"
                autoFocus
              />
            </label>
            <label className="github-import-modal__label">
              Path filter (optional)
              <input
                className="github-import-modal__input"
                value={pathPrefix}
                onChange={(e) => setPathPrefix(e.target.value)}
                placeholder="algorithms/math"
              />
            </label>
            <p className="github-import-modal__hint">
              Paste a folder URL (…/tree/main/path) to import that folder only. Up to 40 source
              files; skips node_modules, build output, and binaries.
            </p>
          </>
        )}

        {(step === 'preview' || step === 'importing') && preview && (
          <div className="github-import-modal__preview">
            <p className="github-import-modal__preview-source">
              <i className="ti ti-brand-github" aria-hidden="true" />
              {preview.fullName}
              <span className="github-import-modal__preview-source-meta">
                {preview.pathPrefix ? `${preview.pathPrefix} · ` : ''}
                {preview.language}
                {preview.filesSkipped > 0 ? ` · ${preview.filesSkipped} skipped` : ''}
              </span>
            </p>

            <label className="github-import-modal__label">
              Cookbook name
              <input
                className="github-import-modal__input"
                value={cookbookName}
                onChange={(e) => setCookbookName(e.target.value)}
                placeholder="Cookbook name"
                disabled={loading}
              />
            </label>

            <div className="github-import-modal__snippets-header">
              <span>Snippets</span>
              <span className="github-import-modal__snippets-count">
                {includedCards.length}/{editableCards.length} included
              </span>
            </div>

            <ul className="github-import-modal__file-list">
              {editableCards.map((card) => (
                <li
                  key={card.key}
                  className={`github-import-modal__file-item${
                    card.included ? '' : ' github-import-modal__file-item--excluded'
                  }`}
                >
                  <label className="github-import-modal__file-include">
                    <input
                      type="checkbox"
                      checked={card.included}
                      onChange={() => toggleCardIncluded(card.key)}
                      disabled={loading}
                      aria-label={`Include ${card.path || card.title}`}
                    />
                  </label>
                  <div className="github-import-modal__file-fields">
                    <input
                      className="github-import-modal__snippet-title-input"
                      value={card.title}
                      onChange={(e) => updateCardTitle(card.key, e.target.value)}
                      disabled={loading || !card.included}
                      aria-label={`Snippet title for ${card.path || 'file'}`}
                    />
                    <span className="github-import-modal__file-path" title={card.path}>
                      {card.language}
                      {card.path ? ` · ${card.path}` : ''}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && <p className="github-import-modal__error">{error}</p>}

        <div className="github-import-modal__actions">
          {step === 'preview' && (
            <button
              type="button"
              className="github-import-modal__cancel"
              onClick={() => {
                setStep('form');
                setPreview(null);
                setEditableCards([]);
                setCookbookName('');
                setError('');
              }}
              disabled={loading}
            >
              Back
            </button>
          )}
          {step === 'form' && (
            <button type="button" className="github-import-modal__cancel" onClick={onClose}>
              Cancel
            </button>
          )}
          {step === 'form' ? (
            <button
              type="button"
              className="github-import-modal__submit"
              onClick={handlePreview}
              disabled={!repoUrl.trim() || loading || cookbookBlocked || snippetBlocked}
            >
              {loading ? 'Fetching…' : 'Preview files'}
            </button>
          ) : (
            <button
              type="button"
              className="github-import-modal__submit"
              onClick={handleConfirmImport}
              disabled={!canCreate}
            >
              {loading
                ? 'Creating cookbook…'
                : `Create cookbook (${includedCards.length})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GitHubImportModal;
